// =============================================================================
//  Copyright   : 2020 semantics GmbH
//  Date        : $Date: 2025-02-28 18:03:38 +0100 (Fri, 28 Feb 2025) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 150704 $
//  Purpose     : process acls (acces control lists)
//                these are strings of the form "g:admin,-g:transcriber"
// =============================================================================
// ---Third-party imports

// ---Framework imports
import log from 'uix/logging'

import {get, mapItems, dict, keys, values, isString} from 'ldash'
import {hashObj} from 'uix/utils'

// ---Globals
const ACL_CACHE = {}

const decodeAcl = acl => {
  /* simple version of vls.auth.users._cacheACLs
   */

  let hkey = hashObj(acl)
  if (ACL_CACHE[hkey]) {
    return ACL_CACHE[hkey]
  }

  if (isString(acl)) {
    acl = acl.split(',')
  }

  let decode = s => {
    let positive = !s.startsWith('-')
    if (!positive) {
      s = s.slice(1)
    }
    let [type, value] = s.split(':')
    return {type: type.trim(), value: value.trim(), positive}
  }
  let decoded = acl.map(e => decode(e.trim()))
  let mapping = {
    g: {name: 'groups', allowed: new Set(), forbidden: new Set()},
    c: {name: 'clients', allowed: new Set(), forbidden: new Set()},
  }
  for (let {type, value, positive} of decoded) {
    mapping[type][positive ? 'allowed' : 'forbidden'].add(value)
  }
  let result = dict(
    values(mapping).map(({name, allowed, forbidden}) => [name, {allowed, forbidden}])
  )

  ACL_CACHE[hkey] = result
  return result
}

// ---Exports
export const checkAcl = (rawacl, {client, groups, localgroups}, dname) => {
  /* simple version of vls.auth.users.processACLs
    returns sucess: true/false
  */
  let acl = decodeAcl(rawacl)
  let forbidden = false
  if (acl.clients.allowed.size > 0 && !acl.clients.allowed.has(client)) {
    // beats all remaining checks
    forbidden = true
  } else if (acl.groups.allowed.size > 0) {
    // satisfying allowed_groups sets delete back to False
    forbidden = true
    // in initializeTemplating, localgroups are already joined into groups
    let lgroups = localgroups?.[dname] || []
    let intersects = [...groups, ...lgroups].some(x => acl.groups.allowed.has(x))
    if (intersects) {
      forbidden = false
    }
  }

  // now check blacklists - if the user has satisfied the conditions so far
  if (!forbidden) {
    if (acl.clients.forbidden.size > 0 && acl.clients.forbidden.has(client)) {
      forbidden = true
    } else if (acl.groups.forbidden.size > 0) {
      let intersects = [...groups].filter(x => acl.groups.forbidden.has(x)).length > 0
      if (intersects) {
        forbidden = true
      }
    }
  }
  return !forbidden
}
